import { AbstractControl, ValidationErrors } from '@angular/forms'
import { EmailFieldTypes } from '@app/v2/shared/enums/field.enum'
import freeDomainsList from 'free-email-domains'

export class EmailValidator {
  /** Regex used for validation */
  static regExp = /^(([^<>()[\]\\.,;:\s@']+(\.[^<>()[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

  /**
   * Returns true if the given email address belongs to a free/consumer email provider
   * @param email full email address
   */
  static isFreeEmail(email: string): boolean {
    const domain = email?.split('@')[1]?.toLowerCase()
    return domain ? freeDomainsList.includes(domain) : false
  }

  /**
   * Applies validation logic
   * @param control Form control
   * @param domains array of allowed or banned domains
   * @param restrictionType type of restriction (ALLOW_SPECIFIC, BAN_SPECIFIC, ALLOW_ALL, BAN_FREE)
   * @param requiredOnline boolean indicating if the email is required online
   * @returns error or null
   */
  static validate(control: AbstractControl, domains: string[] = [], restrictionType: string = EmailFieldTypes.ALLOW_ALL, required: boolean = true): ValidationErrors | null {
    const MAX_LOCAL_PART_CHAR = 64
    const MAX_EMAIL_CHAR = 200

    if (!control.value && !required) return null
    if (!control.value && required) return { required: true }

    if (control.value.length > MAX_EMAIL_CHAR) {
      return {
        maxLength: `Email must be less than ${MAX_EMAIL_CHAR} characters`
      }
    }

    const [localPart, domainPart] = control.value.split('@')
    if (localPart.length > MAX_LOCAL_PART_CHAR) {
      return {
        maxLength: `Local part of email must be less than ${MAX_LOCAL_PART_CHAR} characters`
      }
    }

    if (!EmailValidator.regExp.test(control.value)) {
      return { emailFormat: 'Invalid email format.' }
    }

    const lowercasedDomainPart = domainPart?.toLowerCase()

    if (domains.length) {
      if (restrictionType === 'ALLOW_SPECIFIC' && !domains.includes(lowercasedDomainPart)) {
        return { domainInvalid: 'Email domain is not allowed' }
      }

      if (restrictionType === 'BAN_SPECIFIC' && domains.includes(lowercasedDomainPart)) {
        return { domainInvalid: 'Email domain is not allowed' }
      }
    }

    if (restrictionType === 'BAN_FREE' && freeDomainsList.includes(lowercasedDomainPart)) {
      return { freeDomainEmail: true }
    }

    return null
  }
}
